/**
 * Opciones curadas para el BottomSheet de acción destacada (mobile).
 * Listas distintas retail vs empresa, filtradas por permisos y config flags.
 * Alineado a `v3/utils/dashboard/buildDashboardShortcuts.js`.
 */
import { push } from "react-router-redux";

import * as config from "util/config";
import * as i18n from "util/i18n";
import getTransferPath from "util/transfers";

import { actions as fundcorpActions } from "reducers/fundcorp";
import { actions as servicePaymentsActions } from "reducers/servicePayments";
import { getServicePaymentsPath, isServicePaymentsPath } from "util/servicePaymentsRoutes";
import { buildAddLimitRequestLocation } from "util/addLimitRequestNavigation";

/**
 * @param {object} opts
 * @param {import("redux").Dispatch} opts.dispatch
 * @param {object|null} opts.activeEnvironment
 * @param {boolean} opts.isRetailEnvironment
 * @param {boolean} opts.featureSignatureDispatchControl
 * @returns {Array<{ id: string, icon: string, label: string, onClick: Function }>}
 */
export default function buildFeaturedActionOptions({
    dispatch,
    activeEnvironment,
    isRetailEnvironment,
    featureSignatureDispatchControl,
    fundcorpAccountsCount,
    currentPathname = "/v3/dashboard",
}) {
    if (!activeEnvironment) {
        return [];
    }

    const perm = activeEnvironment.permissions || {};

    const showCorporateCardSummary = config.getBoolean("frontend.show.corporateCard.summary", false);
    const showMultipleTransfers = config.getBoolean("frontend.show.MultipleTransfers.functionalities");
    const currencyExchangeEnabled = config.getBoolean("frontend.foreign.currency.trading.enable", false);
    const directDebitMVP1Enable = config.getBoolean("frontend.show.DirectDebitRequest.MVP1.functionalities", false);
    const timeExtendedPlazoFijo = config.getBoolean("frontend.PlazoFijo.timeExtended", false);

    const hasCurrencyExchangePermission =
        perm["investment.currencyExchange.buy"] ||
        perm["investment.currencyExchange.sell"] ||
        perm["investment.currencyExchange"];

    const payServicesPath = getServicePaymentsPath();

    const payServicesVisible = Boolean(perm["pay.afip"] && perm["service.payments.view"]);

    const setPayServicesEntryPath = () => {
        const p = currentPathname || "/v3/dashboard";
        if (!isServicePaymentsPath(p)) {
            dispatch(servicePaymentsActions.setModuleEntryPath(p));
        }
    };
    const transferPath = getTransferPath("thirdParties");
    const transferVisible = Boolean(perm.transferThirdParties);

    const multipleTransferVisible = Boolean(showMultipleTransfers && perm.transferMultiple);

    const fundSubscribeRescueVisible = Boolean(perm.fundcorpOperations);
    const fundSummariesVisible = Boolean(perm.fundcorpInformation);
    const fundSummariesPath = "/fundInformation";

    const depositConstituteVisible = Boolean(perm.depositsConstitute || perm.deposits);
    const depositConstitutePath = timeExtendedPlazoFijo ? "/v2/depositsConstitute" : "/depositsConstitute";

    const limitShortcutVisible = config.getBoolean("frontend.temporarilyincreaselimit.functionalities", false);

    const fxVisible = Boolean(currencyExchangeEnabled && hasCurrencyExchangePermission);
    const fxPath = "/v2/investment/currencyExchange";

    const vepLabelKey = "dashboard.v3.shortcut.paymentAFIP";

    let defs = [];

    if (isRetailEnvironment) {
        defs = [
            {
                id: "approve-debin",
                labelKey: "dashboard.v3.shortcut.approveDebin",
                icon: "BagDollar",
                path: "/payments/payDebin",
                visible: Boolean(perm["pay.debin"]),
            },
            {
                id: "pay-services",
                labelKey: "dashboard.v3.shortcut.payServices",
                icon: "CoinDollar",
                path: payServicesPath,
                visible: payServicesVisible,
                onBeforeNavigate: () => {
                    setPayServicesEntryPath();
                    dispatch(servicePaymentsActions.setServiceSelected());
                },
            },
            {
                id: "fund-subscribe",
                labelKey: "dashboard.v3.shortcut.fundSubscribe",
                icon: "StocksUp",
                path: "/fundcorpOperations/subscribe",
                visible: fundSubscribeRescueVisible,
            },
            {
                id: "fund-rescue",
                labelKey: "dashboard.v3.shortcut.fundRescue",
                icon: "RescueDolar",
                path: "/fundcorpOperations/rescue",
                visible: fundSubscribeRescueVisible && fundcorpAccountsCount > 0,
            },
            {
                id: "currency-exchange",
                labelKey: "dashboard.v3.shortcut.currencyExchange",
                icon: "CoinDollar",
                path: fxPath,
                visible: fxVisible,
            },
            {
                id: "transfer",
                labelKey: "dashboard.v3.shortcut.transfers",
                icon: "TransferAlt",
                path: transferPath,
                visible: transferVisible,
            },
            {
                id: "deposit-constitute",
                labelKey: "dashboard.v3.shortcut.depositsConstitute",
                icon: "AddCircle",
                path: depositConstitutePath,
                visible: depositConstituteVisible,
            },
            {
                id: "limit-temp",
                labelKey: "dashboard.v3.shortcut.temporaryLimitIncrease",
                icon: "AddLimit",
                path: "/v2/addlimitrequest",
                visible: limitShortcutVisible,
            },
            {
                id: "fund-summaries",
                labelKey: "dashboard.v3.shortcut.fundInformation",
                icon: "Memo",
                path: fundSummariesPath,
                visible: fundSummariesVisible,
            },
            {
                id: "transfer-multiple",
                labelKey: "dashboard.v3.shortcut.multipleTransfers",
                icon: "Transfer",
                path: "/transfer/multiple",
                visible: multipleTransferVisible,
            },
            {
                id: "pay-vep",
                labelKey: vepLabelKey,
                icon: "CoinDollar",
                path: payServicesPath,
                visible: payServicesVisible,
                onBeforeNavigate: () => {
                    setPayServicesEntryPath();
                    dispatch(servicePaymentsActions.setPaymentSelected());
                },
            },
        ];
    } else {
        defs = [
            {
                id: "pendingDispatch",
                labelKey: "dashboard.v3.shortcut.authorizations",
                icon: "WritingPending",
                path: "/pendingTransaction/list",
                visible: Boolean(featureSignatureDispatchControl),
            },
            {
                id: "emit-echeq",
                labelKey: "dashboard.v3.shortcut.emitEcheq",
                icon: "EmitCheq",
                path: "/echeqs/emit",
                visible: Boolean(perm["operate.echeqs"]),
            },
            {
                id: "fund-subscribe",
                labelKey: "dashboard.v3.shortcut.fundSubscribe",
                icon: "StocksUp",
                path: "/fundcorpOperations/subscribe",
                visible: fundSubscribeRescueVisible,
            },
            {
                id: "fund-rescue",
                labelKey: "dashboard.v3.shortcut.fundRescue",
                icon: "RescueDolar",
                path: "/fundcorpOperations/rescue",
                visible: fundSubscribeRescueVisible && fundcorpAccountsCount > 0,
            },
            {
                id: "pay-vep",
                labelKey: vepLabelKey,
                icon: "CoinDollar",
                path: payServicesPath,
                visible: payServicesVisible,
                onBeforeNavigate: () => {
                    setPayServicesEntryPath();
                    dispatch(servicePaymentsActions.setPaymentSelected());
                },
            },
            {
                id: "corp-card",
                labelKey: "menu.corporateCard",
                icon: "CreditCard",
                path: "/v2/corporateCard/accountStatements",
                visible: Boolean(showCorporateCardSummary && perm["corporateCard.summary"]),
            },
            {
                id: "transfer",
                labelKey: "dashboard.v3.shortcut.transfers",
                icon: "TransferAlt",
                path: transferPath,
                visible: transferVisible,
            },
            {
                id: "transfer-multiple",
                labelKey: "dashboard.v3.shortcut.multipleTransfers",
                icon: "Transfer",
                path: "/transfer/multiple",
                visible: multipleTransferVisible,
            },
            {
                id: "pay-services",
                labelKey: "dashboard.v3.shortcut.payServices",
                icon: "CoinDollar",
                path: "/newPayment",
                visible: payServicesVisible,
            },
            {
                id: "request-debin",
                labelKey: "dashboard.v3.shortcut.requestDebin",
                icon: "BagDollar",
                path: "/payments/payDebin",
                visible: Boolean(perm["pay.debin"]),
            },
            {
                id: "currency-exchange",
                labelKey: "dashboard.v3.shortcut.currencyExchange",
                icon: "CoinDollar",
                path: fxPath,
                visible: fxVisible,
            },
            {
                id: "deposit-constitute",
                labelKey: "dashboard.v3.shortcut.depositsConstitute",
                icon: "AddCircle",
                path: depositConstitutePath,
                visible: depositConstituteVisible,
            },
            {
                id: "limit-temp",
                labelKey: "dashboard.v3.shortcut.temporaryLimitIncrease",
                icon: "AddLimit",
                path: "/v2/addlimitrequest",
                visible: limitShortcutVisible,
            },
            {
                id: "direct-debit",
                labelKey: "dashboard.v3.shortcut.directDebit",
                icon: "AddCircle",
                path: "/v2/directDebit/create",
                visible: Boolean(perm["directDebit.access.menu"] && directDebitMVP1Enable),
            },
            {
                id: "fund-summaries",
                labelKey: "dashboard.v3.shortcut.fundInformation",
                icon: "Memo",
                path: fundSummariesPath,
                visible: fundSummariesVisible,
            },
        ];
    }

    return defs
        .filter((d) => d.visible && d.path)
        .map((d) => ({
            id: d.id,
            icon: d.icon,
            label: i18n.get(d.labelKey),
            onClick: () => {
                if (d.onBeforeNavigate) {
                    d.onBeforeNavigate();
                }
                const returnPath =
                    typeof currentPathname === "string" && currentPathname.startsWith("/")
                        ? currentPathname
                        : "/v3/dashboard";
                if (d.id === "fund-subscribe") {
                    dispatch(fundcorpActions.getInvestorProfile("/fundcorpOperations/subscribe", returnPath));
                    return;
                }
                if (d.id === "fund-rescue") {
                    dispatch(
                        push({
                            pathname: d.path,
                            state: { transition: "transition-drill-out", return: returnPath },
                        }),
                    );
                    return;
                }
                if (d.id === "fund-summaries") {
                    dispatch(
                        push({
                            pathname: d.path,
                            state: { return: returnPath },
                        }),
                    );
                    return;
                }
                if (d.id === "limit-temp") {
                    dispatch(push(buildAddLimitRequestLocation(returnPath)));
                    return;
                }
                dispatch(push(d.path));
            },
        }));
}
